import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Alert,
  Grid
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const AvailabilitySummary = ({ availabilitySummary, compact = false }) => {
  if (!availabilitySummary) {
    return null;
  }

  const { 
    patientCount, 
    patientCountSufficient = true, 
    variablesRequested, 
    variablesAvailable, 
    variablesUnavailable = [], 
    allVariablesAvailable = variablesUnavailable.length === 0
  } = availabilitySummary;

  // Berechne den Prozentsatz der verfügbaren Variablen
  const variableAvailabilityPercentage = variablesRequested > 0 
    ? Math.round((variablesAvailable / variablesRequested) * 100) 
    : 100;

  // Bestimme den Status der Verfügbarkeit
  let overallStatus = 'success';
  let statusMessage = 'Alle angeforderten Daten sind verfügbar.';
  let statusIcon = <CheckCircleIcon color="success" />;

  if (!patientCountSufficient) {
    overallStatus = 'error';
    statusMessage = 'Nicht genügend Patienten für die Analyse verfügbar.';
    statusIcon = <CancelIcon color="error" />;
  } else if (!allVariablesAvailable) {
    if (variableAvailabilityPercentage < 50) {
      overallStatus = 'error';
      statusMessage = 'Weniger als 50% der angeforderten Variablen sind verfügbar.';
      statusIcon = <CancelIcon color="error" />;
    } else {
      overallStatus = 'warning';
      statusMessage = 'Einige angeforderte Variablen sind nicht verfügbar.';
      statusIcon = <WarningIcon color="warning" />;
    }
  }

  if (compact) {
    return (
      <Box sx={{ mb: 2 }}>
        <Alert 
          severity={overallStatus === 'success' ? 'success' : overallStatus === 'warning' ? 'warning' : 'error'}
          icon={statusIcon}
        >
          {statusMessage}
        </Alert>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Chip 
            icon={patientCountSufficient ? <CheckCircleIcon /> : <CancelIcon />}
            label={`${patientCount} Patienten`}
            color={patientCountSufficient ? "success" : "error"}
            size="small"
            variant="outlined"
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2">Variablen:</Typography>
            <LinearProgress
              variant="determinate"
              value={variableAvailabilityPercentage}
              color={allVariablesAvailable ? "success" : variableAvailabilityPercentage < 50 ? "error" : "warning"}
              sx={{ width: 100, height: 8, borderRadius: 4 }}
            />
            <Typography variant="caption">{variablesAvailable}/{variablesRequested}</Typography>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom>
        Verfügbarkeit der angeforderten Daten
      </Typography>
      
      <Alert 
        severity={overallStatus === 'success' ? 'success' : overallStatus === 'warning' ? 'warning' : 'error'}
        icon={statusIcon}
        sx={{ mb: 2 }}
      >
        {statusMessage}
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Patienten
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {patientCountSufficient ? 
                <CheckCircleIcon color="success" /> : 
                <CancelIcon color="error" />
              }
              <Typography>
                {patientCount} Patienten verfügbar
              </Typography>
            </Box>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Variablen
            </Typography>
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">
                  {variablesAvailable} von {variablesRequested} verfügbar
                </Typography>
                <Typography variant="body2">
                  {variableAvailabilityPercentage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={variableAvailabilityPercentage}
                color={allVariablesAvailable ? "success" : variableAvailabilityPercentage < 50 ? "error" : "warning"}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {!allVariablesAvailable && variablesUnavailable.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Nicht verfügbare Variablen:
          </Typography>
          <List dense>
            {variablesUnavailable.map((variable, index) => (
              <ListItem key={index}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CancelIcon color="error" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={variable} />
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <InfoIcon color="info" fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          Diese Informationen können die Preisverhandlung beeinflussen.
        </Typography>
      </Box>
    </Paper>
  );
};

export default AvailabilitySummary;
